import CheckIcon from '@mdi/svg/svg/check.svg?react';
import ChevronDownIcon from '@mdi/svg/svg/chevron-down.svg?react';
import SidekickIcon from '@mdi/svg/svg/creation.svg?react';
import InfoIcon from '@mdi/svg/svg/information-outline.svg?react';
import { useUserSession } from '@solinkcorp/authentication';
import settings from '@solinkcorp/settings';
import {
  Button,
  IconWithTooltip,
  MDIIcon,
  PopoverTooltip,
  usePopoverMenu,
} from '@solinkcorp/solink-components';
import { setFilter, type SpotCheckMode } from 'actions/filterActions';
import classNames from 'classnames';
import SelectedLocations from 'components/LocationSelector/SelectedLocations';
import React from 'react';
import useDevices from 'src/hooks/devices/useDevices';
import useSites from 'src/hooks/sites/useSites';
import useLocale from 'src/hooks/useLocale';
import { useDispatch, useSelector } from 'stores/store';
import FeaturesUtil from 'utils/FeaturesUtil';
import { getSiteFeatures } from 'utils/LocationUtil';
import styles from './SpotCheckHeader.module.sass';

interface SpotCheckHeaderProps {
  view: 'templates' | 'spot-check-form';
  selectedLocationIds: string[];
  selectedCameraIds: string[];
  enterCameraSelectMode: () => void;
  resetSpotCheckPanel: (resetCategoryKeys: boolean) => void;
  isNewLook?: boolean;
}

export default function SpotCheckHeader(props: SpotCheckHeaderProps) {
  const {
    view,
    selectedLocationIds,
    selectedCameraIds,
    enterCameraSelectMode,
    resetSpotCheckPanel,
    isNewLook = false,
  } = props;
  const locale = useLocale();
  const { sites } = useSites();
  const dispatch = useDispatch();
  const { devicesBySiteId } = useDevices();
  const user = useUserSession();

  const canCreateSpotChecks = FeaturesUtil.canCreateScheduledAndEventSpotChecks(
    user.clientSettings,
    user.isSolinkUser,
  );

  const isAtLeastOneLicensedLocation = React.useMemo(
    () =>
      sites.some((site) => !!getSiteFeatures(devicesBySiteId[site.id])?.usage?.spotChecks?.enabled),
    [sites, devicesBySiteId],
  );

  const isAllLocationsLicensed = React.useMemo(
    () =>
      sites.every((site) =>
        site.id === 'all-locations'
          ? true
          : !!getSiteFeatures(devicesBySiteId[site.id])?.usage?.spotChecks?.enabled,
      ),
    [sites, devicesBySiteId],
  );

  React.useEffect(() => {
    if (!canCreateSpotChecks || !isAtLeastOneLicensedLocation) {
      dispatch(setFilter({ spotCheckMode: 'live-preview' }));
    }
  }, [canCreateSpotChecks, dispatch, isAtLeastOneLicensedLocation]);

  const modes: { id: SpotCheckMode; label: string }[] = React.useMemo(
    () => [
      { id: 'scheduled', label: locale.scheduledAIChat.scheduledSpotCheck },
      { id: 'event', label: locale.scheduledAIChat.eventSpotCheck },
      { id: 'live-preview', label: locale.scheduledAIChat.liveSpotCheck },
    ],
    [locale],
  );

  const spotCheckMode = useSelector((state) => state.filter.spotCheckMode);

  const currentMode = modes.find((mode) => mode.id === spotCheckMode);

  const [popoverModeSelection, togglePopoverModeSelection] = usePopoverMenu({
    placement: 'bottom',
  });

  const hasSelectedCameras = selectedCameraIds?.length > 0;

  const selectMode = (id: SpotCheckMode) => {
    dispatch(setFilter({ spotCheckMode: id }));
    togglePopoverModeSelection();
    resetSpotCheckPanel(id === 'live-preview');
  };

  const isSpotCheckForm = view === 'spot-check-form';
  const isLivePreviewMode = spotCheckMode === 'live-preview';

  const viewOnlySelectMode =
    isSpotCheckForm || !canCreateSpotChecks || !isAtLeastOneLicensedLocation;
  const displayInfoIcon = isLivePreviewMode && viewOnlySelectMode;
  const displayIcon = !isSpotCheckForm || (isSpotCheckForm && displayInfoIcon);

  /*
    The Upgrade text should be displayed in any mode where these requirements are met
    - The user does not have access to create spot checks (canCreateScheduledAndEventSpotChecks flag is false)
    - Not all of the locations the user is accessing has spot checks enabled
  */
  const shouldDisplayUpgrade = !isAllLocationsLicensed || !canCreateSpotChecks;

  return (
    <div className={styles.spotCheckHeader} id="spot-check-panel">
      <div className={styles.spotCheckTitle}>
        <MDIIcon icon={SidekickIcon} fill="black" />
        {!isNewLook && <h2>{locale.scheduledAIChat.sidekick}</h2>}
        <Button
          className={classNames(styles.selectModeButton, {
            [styles.viewOnly]: viewOnlySelectMode,
            [styles.newLook]: isNewLook,
          })}
          refFunc={popoverModeSelection.setTriggerRef}
          onClick={viewOnlySelectMode ? undefined : togglePopoverModeSelection}
          icon={
            displayIcon && <MDIIcon icon={displayInfoIcon ? InfoIcon : ChevronDownIcon} size={20} />
          }
          iconPosition="back"
          text={currentMode?.label}
          tooltip={
            displayInfoIcon ? locale.scheduledAIChat.schedulingSpotChecksDisabled : undefined
          }
        />
      </div>
      <PopoverTooltip {...popoverModeSelection}>
        <ul className={styles.spotCheckModeSelectionList}>
          {modes.map((mode) => (
            <li key={mode.id}>
              <button onClick={() => selectMode(mode.id)}>
                <div className={styles.selectedWrapper}>
                  {mode.id === spotCheckMode && <MDIIcon icon={CheckIcon} size={20} />}
                </div>
                <span>{mode.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </PopoverTooltip>
      {hasSelectedCameras && (
        <button className={styles.spotCheckFilters} onClick={enterCameraSelectMode}>
          <SelectedLocations
            locationIds={selectedLocationIds}
            locationTags={undefined}
            locationsList={sites}
            maxCharacterLimit={40}
          />
          <span>-</span>
          <span>
            {selectedCameraIds.length} {locale.camera.cameras}
          </span>
        </button>
      )}
      {shouldDisplayUpgrade && (
        <a href={settings.solinkUpgradeUrl} target="_blank" rel="noopener noreferrer">
          {locale.cloudStorage.upgrade}
          <IconWithTooltip
            icon={InfoIcon}
            iconSize={20}
            title={locale.scheduledAIChat.upgradeToMoreSpotChecksInfo}
          />
        </a>
      )}
    </div>
  );
}
